import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import useApi from "./useApi";
import { toast } from "react-toastify";
import {
    IconButton,
    Modal,
    Box,
    Typography,
    Button,
    TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

const Packages = () => {
    const { projectId } = useParams();
    const [newPackageName, setNewPackageName] = useState("");
    const [newPackageDescription, setNewPackageDescription] = useState("");
    const [currentPackage, setCurrentPackage] = useState(null);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [editPackageName, setEditPackageName] = useState("");
    const [editPackageDescription, setEditPackageDescription] = useState("");
    const [{ data: packages, loading }, callApi] = useApi();

    useEffect(() => {
        callApi({ url: `/api/projects/${projectId}/packages` });
    }, [projectId, callApi]);

    const handleCreatePackage = async (e) => {
        e.preventDefault();
        callApi({
            url: `/api/projects/${projectId}/packages`,
            method: "POST",
            body: { name: newPackageName, description: newPackageDescription },
        }).then(() => {
            toast.success("Package created successfully!");
            setNewPackageName("");
            setNewPackageDescription("");
            callApi({ url: `/api/projects/${projectId}/packages` });
        });
    };

    const openDeleteModalHandler = (pkg) => {
        setCurrentPackage(pkg);
        setOpenDeleteModal(true);
    };

    const handleDeletePackage = () => {
        if (currentPackage) {
            callApi({
                url: `/api/projects/${projectId}/packages/${currentPackage.id}`,
                method: "DELETE",
            })
                .then(() => {
                    toast.success("Package deleted successfully!");
                    setOpenDeleteModal(false);
                    callApi({ url: `/api/projects/${projectId}/packages` }); // Re-fetch packages
                })
                .catch((error) => {
                    toast.error("Failed to delete package.");
                    console.error("Delete package error:", error);
                });
        }
    };

    const openEditModalHandler = (pkg) => {
        setCurrentPackage(pkg);
        setEditPackageName(pkg.name);
        setEditPackageDescription(pkg.description ? pkg.description : "");
        setOpenEditModal(true);
    };

    const handleEditPackage = (e) => {
        e.preventDefault();
        if (currentPackage) {
            callApi({
                url: `/api/projects/${projectId}/packages/${currentPackage.id}`,
                method: "PUT",
                body: {
                    name: editPackageName,
                    description: editPackageDescription,
                },
            })
                .then(() => {
                    toast.success("Package updated successfully!");
                    setOpenEditModal(false);
                    callApi({ url: `/api/projects/${projectId}/packages` }); // Re-fetch packages
                })
                .catch((error) => {
                    toast.error("Failed to update package.");
                    console.error("Update package error:", error);
                });
        }
    };

    return (
        <div className="content-container">
            <h2>Packages for Project {projectId}</h2>
            {loading && <div>Loading...</div>}
            <form onSubmit={handleCreatePackage} className="form-container">
                <input
                    type="text"
                    placeholder="Package Name"
                    value={newPackageName}
                    onChange={(e) => setNewPackageName(e.target.value)}
                    required
                    className="form-input"
                />
                <textarea
                    placeholder="Package Description"
                    value={newPackageDescription}
                    onChange={(e) => setNewPackageDescription(e.target.value)}
                    required
                    className="form-textarea"
                />
                <button type="submit" className="form-button">
                    Create New Package
                </button>
            </form>
            <ul className="item-list">
                {Array.isArray(packages) ? (
                    packages.map((pkg) => (
                        <li key={pkg.id} className="list-item">
                            <Link
                                to={`/projects/${projectId}/packages/${pkg.id}/subcontractors`}
                                className="item-link"
                            >
                                {pkg.name}
                            </Link>
                            <IconButton
                                onClick={() => openEditModalHandler(pkg)}
                                size="small"
                            >
                                <EditIcon />
                            </IconButton>
                            <IconButton
                                onClick={() => openDeleteModalHandler(pkg)}
                                size="small"
                            >
                                <DeleteIcon />
                            </IconButton>
                            {/*<Button*/}
                            {/*    variant="contained"*/}
                            {/*    color="primary"*/}
                            {/*    size="small"*/}
                            {/*    component={Link}*/}
                            {/*    to={`/projects/${projectId}/packages/${pkg.id}/master_list`}*/}
                            {/*    style={{*/}
                            {/*        textDecoration: "none",*/}
                            {/*        marginLeft: "auto",*/}
                            {/*    }}*/}
                            {/*>*/}
                            {/*    Master List*/}
                            {/*</Button>*/}
                        </li>
                    ))
                ) : (
                    <p>No packages found.</p>
                )}
                <Modal
                    open={openDeleteModal}
                    onClose={() => setOpenDeleteModal(false)}
                    aria-labelledby="delete-modal-title"
                    aria-describedby="delete-modal-description"
                >
                    <Box className="modal">
                        <Typography id="delete-modal-title" variant="h6">
                            Confirm Package Deletion
                        </Typography>
                        <Typography
                            id="delete-modal-description"
                            sx={{ mt: 2 }}
                        >
                            Are you sure you want to delete this package?
                        </Typography>
                        <Button onClick={handleDeletePackage} color="error">
                            Delete
                        </Button>
                        <Button onClick={() => setOpenDeleteModal(false)}>
                            Cancel
                        </Button>
                    </Box>
                </Modal>
                <Modal
                    open={openEditModal}
                    onClose={() => setOpenEditModal(false)}
                    aria-labelledby="edit-modal-title"
                    aria-describedby="edit-modal-description"
                >
                    <Box
                        className="modal"
                        component="form"
                        onSubmit={handleEditPackage}
                    >
                        <Typography id="edit-modal-title" variant="h6">
                            Edit Package
                        </Typography>
                        <TextField
                            margin="dense"
                            id="name"
                            label="Package Name"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={editPackageName}
                            onChange={(e) => setEditPackageName(e.target.value)}
                            required
                        />
                        <TextField
                            margin="dense"
                            id="description"
                            label="Package Description"
                            type="text"
                            fullWidth
                            variant="outlined"
                            multiline
                            rows={4}
                            value={editPackageDescription}
                            onChange={(e) =>
                                setEditPackageDescription(e.target.value)
                            }
                        />
                        <Button type="submit" color="primary">
                            Update
                        </Button>
                        <Button onClick={() => setOpenEditModal(false)}>
                            Cancel
                        </Button>
                    </Box>
                </Modal>
            </ul>
        </div>
    );
};

export default Packages;
